/* eslint-disable @typescript-eslint/no-unused-vars */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { IMachineType } from 'projects/common/src/app/interfaces/machine-type';
import { CacheService, ParameterHandlerService } from 'projects/common/src/app/services';
import { isEquivalent } from 'projects/common/src/app/services/utils/utilities';
import { combineLatest } from 'rxjs';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { WidgetRequestComponent } from 'src/app/modules/widget/components/widget-request/widget-request.component';
import { ICachedWidgetResutData } from 'src/app/modules/widget/interfaces/cached-widget-result-data';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { CommunicationService } from 'src/app/services/communication';
import { IMachineContext } from '../../../../../../projects/common/src/app/interfaces/machine-context';
import { IUser } from '../../../../../../projects/common/src/app/interfaces/user';
import { IShift } from '../../../core/interfaces/shift';
import { IWidgetGetter } from '../../../widget/interfaces/widget-data/widget-getter';
import { IStoredProcedureInputs } from '../../interfaces/chart-widget';
import { takeWhile, tap } from 'rxjs/operators';

@Component({
  selector: 'app-dashboard-widget-basic',
  templateUrl: './dashboard-widget-basic.component.html',
  styleUrls: ['./dashboard-widget-basic.component.scss'],
})
export class DashboardWidgetBasicComponent extends WidgetRequestComponent implements OnInit, OnDestroy {
  public hasData: boolean;
  protected params: IWidgetGetter;

  constructor(
    protected progressBarService: BooleanStateService,
    protected communication: CommunicationService,
    protected parameterService: ParameterHandlerService,
    protected cacheService: CacheService,
    protected machineContext: MachineContextService,
    protected screenViewService: ScreenViewService,
  ) {
    super(progressBarService, communication, screenViewService);
  }

  public ngOnInit(): void {
    super.ngOnInit();
  }

  /**
   * Function to build needed params
   */
  protected buildRequestParams(): IStoredProcedureInputs {
    const params: { [key: string]: any } = {};
    const parameter = this.parameterService.parameters;

    if (this.params?.hasCompany) {
      const userData = parameter.item('user-data').value as IUser;
      if (userData?.company?.id) {
        params.companyId = String(userData.company.id);
      } else {
        console.warn('Company data is not available');
      }
    }

    if (this.params?.hasDate) {
      const date = parameter.item('date').value;
      if (date) {
        params.inputDate = String(date);
      } else {
        console.warn('Date is not available');
      }
    }

    if (this.params?.hasMachineType) {
      const machineContext = parameter.item('machine-context').value as IMachineContext;
      const machineCollection = parameter.item('machine-collection').value as IMachineType[];
      if (machineContext?.name && machineCollection?.length) {
        const machine = machineCollection.find((value) => String(value.name).toLowerCase() === String(machineContext.name).toLowerCase());
        if (machine?.machineTypeId) {
          params.machineTypeId = machine.machineTypeId;
        } else {
          console.warn('Machine Type ID is not available');
        }
      } else {
        console.warn('Machine context or collection is not available');
      }
    }

    if (this.params?.hasShift) {
      let shift = parameter.item('shift').value as IShift;
      if (!shift?.shiftId) {
        console.warn('Shift ID not available, attempting to retrieve from sessionStorage.');
        this.setShiftFromStorage();
        shift = parameter.item('shift').value as IShift;
      }

      if (shift?.shiftId) {
        params.shiftId = String(shift.shiftId);
      } else {
        console.warn('Shift ID could not be determined.');
      }
    }

    if (this.params?.hasZone) {
      const zone = this.cacheService.session('selectedZone');
      if (zone?.zoneId) {
        params.zoneId = zone.zoneId;
      } else {
        console.warn('Zone ID is not available');
      }
    }

    if (this.params?.hasOrderBy) {
      const orderBy = this.cacheService.local('orderBy');
      params.orderBy = orderBy || 1;
    }

    return params;
  }

  /**
   * Function to build view
   *
   * @param data Data to save
   */
  protected buildView(data: any): void {
    super.buildView(data);
    const dataToSave = {
      data,
      query: this.buildRequestParams(),
    } as ICachedWidgetResutData;
    this.cacheService.saveSession(this.data.title, dataToSave);

    const previous = this.cacheService.session(this.data.title) as ICachedWidgetResutData;

    if (previous) {
      const currentParams = this.buildRequestParams();
      if (isEquivalent(previous.query, currentParams)) {
        this.drawView(previous.data);
      }
    }
  }

  /**
   * Try to set the shift from the session storage if it is not available
   */
  private setShiftFromStorage(): void {
    const shiftsJson = sessionStorage.getItem('shift-collection');
    if (!shiftsJson) {
      console.warn('No hay turnos disponibles en sessionStorage.');
      return;
    }

    try {
      const shifts: IShift[] = JSON.parse(shiftsJson);
      const currentShift = this.findCurrentShift(shifts);

      if (currentShift) {
        this.parameterService.setParameter('shift', currentShift);
        this.initializeRemoteRequest();
      } else {
        console.warn('No se pudo determinar el turno actual.');
      }
    } catch (error) {
      console.error('Error al parsear shift-collection desde sessionStorage:', error);
    }
  }

  /**
   * Find the current shift based on the current time
   */
  private findCurrentShift(shifts: IShift[]): IShift | null {
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes(); // Convertir a minutos totales del día

    return (
      shifts.find((shift) => {
        const startTime = this.parseTime(shift.startTime);
        const endTime = this.parseTime(shift.endTime);

        if (startTime === null || endTime === null) {
          console.warn(`No se pudo obtener startTime o endTime para shiftId: ${shift.shiftId}`);
          return false;
        }

        if (startTime <= endTime) {
          // Current time is between start and end time
          return currentTime >= startTime && currentTime <= endTime;
        }
        // Shift spans midnight
        return currentTime >= startTime || currentTime <= endTime;
      }) || null
    );
  }

  /**
   * Convert object { hours, minutes } or string "HH:mm" to total minutes from midnight 00:00
   */
  private parseTime(time: { hours: number; minutes: number } | string | undefined): number | null {
    if (!time) {
      return null;
    }

    if (typeof time === 'string') {
      // Case when `time` is a string "HH:mm"
      const [hours, minutes] = time.split(':').map(Number);
      if (Number.isNaN(hours) || Number.isNaN(minutes)) {
        console.warn(`Formato de tiempo inválido: ${time}`);
        return null;
      }
      return hours * 60 + minutes;
    }

    if (typeof time === 'object' && 'hours' in time && 'minutes' in time) {
      // Case when `time` is an object { hours, minutes }
      return time.hours * 60 + time.minutes;
    }

    console.warn(`Tipo de dato inesperado para el tiempo:`, time);
    return null;
  }

  protected drawView(data: any): void {
    // Do something
  }

  /**
   * On destroy component
   */
  public ngOnDestroy(): void {
    this.componentDestroyed = true;
    super.ngOnDestroy();
  }
}
